// Unified Force Finish Function
// Handles all force finish operations: design, estimation, delivery, and all phases
// Creates missing estimator/designer records and logs all actions

import { supabase } from '../../config/supabase';
import { sendInTransitNotificationSMS, sendSMS } from './sms';

/**
 * Unified force finish function for all types
 * @param {string} jobId - The job ID
 * @param {Object} userProfile - The admin user profile object with id, name, and role
 * @param {string} type - The type: "design", "estimation", "delivery", or "all"
 * @param {string|null} quoteType - The quote type (roof, wall, floor) - required for design type
 * @returns {Promise<Object>} Result object
 */
export const forceFinish = async (jobId, userProfile, type, quoteType = null, assignment = null) => {
    try {
        const now = new Date().toISOString();
        const adminName = userProfile?.name || userProfile?.full_name || 'Admin';
        const adminUserId = userProfile?.id;
        const adminRole = userProfile?.role || 'admin';
        const finishingNote = `Force finished by ${adminName} on ${new Date().toLocaleString('en-AU')}`;

        // Validate inputs
        if (!jobId || !adminUserId) {
            throw new Error('Missing required parameters');
        }

        if (type === 'design' && !quoteType) {
            throw new Error('Quote type is required for design force finish');
        }

        if (!userProfile?.role || !['admin', 'super_admin'].includes(userProfile.role)) {
            throw new Error('Insufficient permissions. Only admins can force finish operations.');
        }

        // Get job details
        const { data: job, error: jobCheckError } = await supabase
            .from('jobs')
            .select('job_type, company_id')
            .eq('id', jobId)
            .single();

        if (jobCheckError) throw jobCheckError;

        let successMessage = '';
        let actionDetails = '';

        switch (type) {
            case 'design':
                await forceFinishDesignLogic(jobId, quoteType, finishingNote, now, userProfile);
                successMessage = `Design force finished for ${quoteType}`;
                actionDetails = `Force finished design for ${quoteType} quote type`;
                break;

            case 'estimation':
                if (!['Estimation Only', 'Design & Estimation'].includes(job.job_type)) {
                    throw new Error('Estimation is not required for this job type');
                }
                await forceFinishEstimationLogic(jobId, finishingNote, now, userProfile);
                successMessage = 'Estimation force finished';
                actionDetails = 'Force finished estimation phase';
                break;

            case 'delivery':
                await forceFinishDeliveryLogic(jobId, assignment, finishingNote, now, userProfile);
                successMessage = 'Delivery force finished';
                actionDetails = 'Force finished delivery phase';
                break;

            case 'all':
                await forceFinishAllLogic(jobId, job, finishingNote, now, userProfile);
                successMessage = 'All phases force finished';
                actionDetails = 'Force finished all phases (design, estimation, delivery)';
                break;

            default:
                throw new Error(`Invalid force finish type: ${type}`);
        }

        // Log the action
        await createActionLog(jobId, userProfile, 'force_finish', actionDetails, quoteType);

        // Update job timestamp to trigger any listeners
        const { error: jobError } = await supabase
            .from('jobs')
            .update({ updated_at: now })
            .eq('id', jobId);

        if (jobError) throw jobError;

        return {
            success: true,
            message: successMessage,
            jobId,
            type,
            quoteType
        };

    } catch (error) {
        console.error('Error in force finish:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Create action log entry
 */
const createActionLog = async (jobId, userProfile, action, details, quoteType = null) => {
    try {
        const { error } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: userProfile.id,
                user_name: userProfile.name || userProfile.full_name || 'Admin',
                user_role: userProfile.role || 'admin',
                action: action,
                details: details,
                quote_type: quoteType,
                timestamp: new Date().toISOString()
            });

        if (error) {
            console.error('Error creating action log:', error);
        }
    } catch (error) {
        console.error('Error in createActionLog:', error);
    }
};

/**
 * Ensure estimator record exists for the job
 */
const ensureEstimatorExists = async (jobId, userProfile, now) => {
    // Check if estimator already exists
    const { data: existingEstimator, error: checkError } = await supabase
        .from('job_estimators')
        .select('id')
        .eq('job_id', jobId)
        .single();

    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw checkError;
    }

    // If no estimator exists, create one
    if (!existingEstimator) {
        const { error: insertError } = await supabase
            .from('job_estimators')
            .insert({
                job_id: jobId,
                user_id: userProfile.id,
                name: userProfile.name || userProfile.full_name || 'Admin',
                assigned_at: now,
                created_at: now,
                updated_at: now
            });

        if (insertError) throw insertError;
    }
};

/**
 * Ensure designer record exists for the job and quote type
 */
const ensureDesignerExists = async (jobId, quoteType, userProfile, now) => {
    // Check if designer already exists for this job and quote type
    const { data: existingDesigner, error: checkError } = await supabase
        .from('job_designers')
        .select('id')
        .eq('job_id', jobId)
        .eq('quote_type', quoteType);

    if (checkError) throw checkError;

    // If no designer exists for this quote type, create one
    if (!existingDesigner || existingDesigner.length === 0) {
        const { error: insertError } = await supabase
            .from('job_designers')
            .insert({
                job_id: jobId,
                quote_type: quoteType,
                user_id: userProfile.id,
                name: userProfile.name || userProfile.full_name || 'Admin',
                assigned_at: now,
                created_at: now,
                updated_at: now
            });

        if (insertError) throw insertError;
    }
};

/**
 * Ensure driver record exists for the job and quote type
 */
const ensureDriverExists = async ({
    jobId,
    quoteType,
    childId = null,
    childUniqueId = null,
    job = null,
    userProfile,
    now
}) => {
    if (!jobId) throw new Error('jobId is required');
    if (!quoteType) throw new Error('quoteType is required for delivery');

    let query = supabase
        .from('job_deliveries')
        .select('id')
        .eq('job_id', jobId)
        .eq('delivery_count', 0);

    // Priority: child_id
    if (childId) {
        query = query.eq('child_id', childId);
    } else {
        query = query
            .is('child_id', null)
            .eq('quote_type', quoteType);
    }

    const { data: existingDriver, error: checkError } = await query.limit(1);

    if (checkError) throw checkError;

    if (!existingDriver || existingDriver.length === 0) {
        const insertPayload = {
            job_id: jobId,
            quote_type: quoteType,
            child_id: childId || null,
            child_unique_id: childId || null,

            is_required: true,
            delivery_status: 'delivered',
            completed: true,
            accepted: true,

            assigned_driver_id: userProfile.id,
            assigned_driver_name: userProfile.name || userProfile.full_name || 'Admin',
            assigned_at: now,
            assigned_by_user_id: userProfile.id,
            assigned_by_name: userProfile.name || userProfile.full_name || 'Admin',

            delivered_at: now,
            delivered_by_user_id: userProfile.id,
            delivered_by_name: userProfile.name || userProfile.full_name || 'Admin',

            // delivery_notes: `Force finished by ${userProfile.name || userProfile.full_name || 'Admin'}`,
            delivery_count: 0,

            created_at: now,
            updated_at: now,

            // optional defaults from job object if available
            delivery_address: job?.customer_address || job?.site_address || null,
            delivery_contact_name: job?.customer_name || job?.contact_name || null,
            delivery_contact_phone: job?.customer_phone || job?.contact_phone || null,
        };

        const { data: inserted, error: insertError } = await supabase
            .from('job_deliveries')
            .insert(insertPayload)
            .select()
            .single();

        if (insertError) throw insertError;

        return inserted;
    }

    return existingDriver[0];
};

/**
 * Force finish design logic
 */
const forceFinishDesignLogic = async (jobId, quoteType, finishingNote, now, userProfile) => {
    // Ensure designer exists for this quote type
    await ensureDesignerExists(jobId, quoteType, userProfile, now);

    // Update the quote design status to completed
    const { error: quoteError } = await supabase
        .from('job_quotes')
        .update({
            design_status: 'completed',
            updated_at: now
        })
        .eq('job_id', jobId)
        .eq('quote_type', quoteType)
        .eq('is_required', true);

    if (quoteError) throw quoteError;

    // Get and update designers
    const { data: designers, error: designersError } = await supabase
        .from('job_designers')
        .select('*')
        .eq('job_id', jobId)
        .eq('quote_type', quoteType)
        .eq('is_completed', false);

    if (designersError) throw designersError;

    if (designers && designers.length > 0) {
        for (const designer of designers) {
            const { error: designerUpdateError } = await supabase
                .from('job_designers')
                .update({
                    is_completed: true,
                    completed_at: now,
                    is_started: true,
                    started_at: designer.started_at || now,
                    finishing_note: designer.finishing_note || finishingNote,
                    completed_by: userProfile.id,
                    completed_by_name: userProfile.name || userProfile.full_name || 'Admin',
                    updated_at: now
                })
                .eq('id', designer.id);

            if (designerUpdateError) throw designerUpdateError;
        }
    }
};

/**
 * Force finish estimation logic
 */
const forceFinishEstimationLogic = async (jobId, finishingNote, now, userProfile) => {
    // Ensure estimator exists
    await ensureEstimatorExists(jobId, userProfile, now);

    // Update all required quotes estimation status to completed
    const { error: quoteError } = await supabase
        .from('job_quotes')
        .update({
            estimation_status: 'completed',
            updated_at: now
        })
        .eq('job_id', jobId)
        .eq('is_required', true)
        .neq('estimation_status', 'not_required');

    if (quoteError) throw quoteError;

    // Get and update estimators
    const { data: estimators, error: estimatorsError } = await supabase
        .from('job_estimators')
        .select('*')
        .eq('job_id', jobId)
        .eq('is_completed', false);

    if (estimatorsError) throw estimatorsError;

    if (estimators && estimators.length > 0) {
        for (const estimator of estimators) {
            const { error: estimatorUpdateError } = await supabase
                .from('job_estimators')
                .update({
                    is_completed: true,
                    completed_at: now,
                    is_started: true,
                    started_at: estimator.started_at || now,
                    finishing_note: estimator.finishing_note || finishingNote,
                    completed_by: userProfile.id,
                    completed_by_name: userProfile.name || userProfile.full_name || 'Admin',
                    updated_at: now
                })
                .eq('id', estimator.id);

            if (estimatorUpdateError) throw estimatorUpdateError;
        }
    }
};

/**
 * Force finish delivery logic
 */
const forceFinishDeliveryLogic = async (jobId, assignment, finishingNote, now, userProfile) => {
    if (!assignment) {
        throw new Error('Assignment context is required for delivery force finish');
    }

    const quoteType = assignment?.quote_info?.quote_type;
    const childId = assignment?.quote_info?.quoteId || assignment?.quote_info?.child_id || null;
    const childUniqueId = assignment?.quote_info?.childUniqueId || null;

    if (!quoteType) {
        throw new Error('Missing quote type for delivery force finish');
    }


    // Get company settings
    const companyId =
        assignment?.company_id ||
        assignment?.companyId ||
        null;

    let companySettings = {};
    let sendTransitSMSonForceDelivery = false;
    let companyName = "";

    if (companyId) {
        const { data: companyData, error: companyError } = await supabase
            .from('companies')
            .select('settings, name')
            .eq('id', companyId)
            .single();

        if (companyError) {
            throw companyError;
        }

        companyName = companyData?.name || null

        try {
            companySettings =
                typeof companyData?.settings === 'string'
                    ? JSON.parse(companyData.settings)
                    : (companyData?.settings || {});
        } catch (err) {
            console.error('Failed to parse company settings:', err);
            companySettings = {};
        }

        sendTransitSMSonForceDelivery = companySettings?.sendTransitSMSonForceDelivery === true;
    }

    // pull some fallback delivery info from the selected job card itself
    const jobLike = {
        customer_name: assignment?.customer_name || null,
        customer_address: assignment?.customer_address || null,
        customer_phone: assignment?.customer_number || null,
        contact_name: assignment?.delivery_contact_name || assignment?.customer_name || null,
        contact_phone: assignment?.delivery_contact_phone || assignment?.customer_phone || null,
    };


    await ensureDriverExists({
        jobId,
        quoteType,
        childId,
        childUniqueId,
        job: jobLike,
        userProfile,
        now
    });

    // update the matching quote
    let quoteUpdate = supabase
        .from('job_quotes')
        .update({
            delivery_status: 'delivered',
            updated_at: now
        })
        .eq('job_id', jobId)
        .eq('quote_type', quoteType)
        .eq('is_required', true);

    if (childId) {
        quoteUpdate = quoteUpdate.eq('id', childId);
    }

    const { error: quoteError } = await quoteUpdate;
    if (quoteError) throw quoteError;

    // update the matching delivery row
    let deliveryUpdate = supabase
        .from('job_deliveries')
        .update({
            delivery_status: 'delivered',
            completed: true,
            accepted: true,
            delivered_at: now,
            delivered_by_user_id: userProfile.id,
            delivered_by_name: userProfile.name || userProfile.full_name || 'Admin',
            assigned_driver_id: userProfile.id,
            assigned_driver_name: userProfile.name || userProfile.full_name || 'Admin',
            assigned_at: now,
            delivery_notes: finishingNote,
            updated_at: now
        })
        .eq('job_id', jobId)
        .eq('delivery_count', 0);

    if (childId) {
        deliveryUpdate = deliveryUpdate.eq('child_id', childId);
    } else {
        deliveryUpdate = deliveryUpdate
            .is('child_id', null)
            .eq('quote_type', quoteType);
    }

    const { error: deliveryError } = await deliveryUpdate;
    if (deliveryError) throw deliveryError;


    // Send SMS if company setting is enabled
    if (sendTransitSMSonForceDelivery && companyId) {
        const phoneNumber =
            assignment?.customer_number ||
            assignment?.customer_phone ||
            assignment?.contact_phone ||
            null;

        if (phoneNumber) {
            const jobLabel =
                assignment?.quote_info?.childUniqueId ||
                assignment?.unique_id ||
                assignment?.uniqueId ||
                jobId;


            try {
                const smsResult = await sendInTransitNotificationSMS(
                    phoneNumber,
                    assignment?.customer_name,
                    assignment?.short_id,
                    assignment?.id,
                    Date.now(),
                    { name: companyName, id: assignment?.companyId || assignment?.company_id },
                    assignment?.unique_id
                );

                if (smsResult?.error) {
                    console.error('Force finish SMS failed:', smsResult.error);
                }
            } catch (smsError) {
                console.error('Force finish SMS failed:', smsError);
            }
        }
    }
};

/**
 * Force finish all phases logic
 */
const forceFinishAllLogic = async (jobId, job, finishingNote, now, userProfile) => {
    // Get all required quote types for this job
    const { data: quotes, error: quotesError } = await supabase
        .from('job_quotes')
        .select('quote_type')
        .eq('job_id', jobId)
        .eq('is_required', true);

    if (quotesError) throw quotesError;

    // Ensure designers exist for all required quote types
    if (quotes && quotes.length > 0) {
        for (const quote of quotes) {
            await ensureDesignerExists(jobId, quote.quote_type, userProfile, now);
        }
    }

    // Force finish all designs
    const { error: quoteDesignError } = await supabase
        .from('job_quotes')
        .update({
            design_status: 'completed',
            updated_at: now
        })
        .eq('job_id', jobId)
        .eq('is_required', true)
        .neq('design_status', 'not_required');

    if (quoteDesignError) throw quoteDesignError;

    // Get and update all designers
    const { data: designers, error: designersError } = await supabase
        .from('job_designers')
        .select('*')
        .eq('job_id', jobId)
        .eq('is_completed', false);

    if (designersError) throw designersError;

    if (designers && designers.length > 0) {
        for (const designer of designers) {
            const { error: designerUpdateError } = await supabase
                .from('job_designers')
                .update({
                    is_completed: true,
                    completed_at: now,
                    is_started: true,
                    started_at: designer.started_at || now,
                    finishing_note: designer.finishing_note || finishingNote,
                    completed_by: userProfile.id,
                    completed_by_name: userProfile.name || userProfile.full_name || 'Admin',
                    updated_at: now
                })
                .eq('id', designer.id);

            if (designerUpdateError) throw designerUpdateError;
        }
    }

    // Force finish estimation if required
    if (['Estimation Only', 'Design & Estimation'].includes(job.job_type)) {
        await forceFinishEstimationLogic(jobId, finishingNote, now, userProfile);
    }

    // Force finish delivery
    await forceFinishDeliveryLogic(jobId, finishingNote, now, userProfile);
};
/**
 * Undo a completed delivery — reverts one job_deliveries row (and its matching
 * job_quotes row) from 'delivered' back to 'scheduled' (driver assigned) or
 * 'not_scheduled' (no driver). Reverses both driver completion and force finish.
 * @param {string} deliveryId - job_deliveries.id of the card's delivery row
 * @param {Object} userProfile - The admin user profile object with id, name, and role
 * @returns {Promise<Object>} Result object
 */
export const undoDeliveryCompletion = async (deliveryId, userProfile) => {
    try {
        const now = new Date().toISOString();

        if (!deliveryId || !userProfile?.id) {
            throw new Error('Missing required parameters');
        }

        if (!userProfile?.role || !['admin', 'super_admin'].includes(userProfile.role)) {
            throw new Error('Insufficient permissions. Only admins can undo delivery completion.');
        }

        const { data: delivery, error: fetchError } = await supabase
            .from('job_deliveries')
            .select('id, job_id, quote_type, delivery_count, child_unique_id, assigned_driver_id, delivery_status')
            .eq('id', deliveryId)
            .single();

        if (fetchError) throw fetchError;

        if (delivery.delivery_status !== 'delivered') {
            throw new Error('This delivery is not completed');
        }

        const revertedStatus = delivery.assigned_driver_id ? 'scheduled' : 'not_scheduled';

        const { error: deliveryError } = await supabase
            .from('job_deliveries')
            .update({
                delivery_status: revertedStatus,
                completed: false,
                delivered_at: null,
                delivered_by_user_id: null,
                delivered_by_name: null,
                updated_at: now
            })
            .eq('id', deliveryId);

        if (deliveryError) throw deliveryError;

        // Matching quote row — same matching rules as confirmPackageReceipt
        let quoteUpdate = supabase
            .from('job_quotes')
            .update({
                delivery_status: revertedStatus,
                updated_at: now
            })
            .eq('job_id', delivery.job_id)
            .eq('quote_type', delivery.quote_type);

        if (delivery.child_unique_id) {
            quoteUpdate = quoteUpdate.eq('id', delivery.child_unique_id);
        } else if ((delivery.delivery_count ?? 0) === 0) {
            quoteUpdate = quoteUpdate.or('delivery_count.is.null,delivery_count.eq.0');
        } else {
            quoteUpdate = quoteUpdate.eq('delivery_count', delivery.delivery_count);
        }

        const { error: quoteError } = await quoteUpdate;
        if (quoteError) throw quoteError;

        await createActionLog(
            delivery.job_id,
            userProfile,
            'undo_delivery_completion',
            `Undid delivery completion (status reverted to ${revertedStatus})`,
            delivery.quote_type
        );

        // Update job timestamp to trigger any listeners
        const { error: jobError } = await supabase
            .from('jobs')
            .update({ updated_at: now })
            .eq('id', delivery.job_id);

        if (jobError) throw jobError;

        return {
            success: true,
            message: 'Delivery completion undone',
            status: revertedStatus
        };
    } catch (error) {
        console.error('Error undoing delivery completion:', error);
        return {
            success: false,
            error: error.message
        };
    }
};
